import {
    Assessment,
    Celebration,
    Check,
    CheckCircle,
    CloudUpload,
    HourglassEmpty,
    NavigateBefore,
    NavigateNext,
    Person,
    Psychology,
    Visibility,
} from '@mui/icons-material';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    IconButton,
    LinearProgress,
    MobileStepper,
    Paper,
    Snackbar,
    Step,
    StepLabel,
    Stepper,
    type Theme,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EEmployeeTalkStatus } from '@sharedInterfaces/IEmployeeTalk';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import OnboardingAgent from '@src/App/Components/_UnderConstruction/Onboarding/components/OnboardingAgent';
import { OnboardingChatButton } from './OnboardingChatButton';
import InnerLayout from '@src/App/Components/Core/Layouts/InnerLayout/InnerLayout';
import LoadingBox from '@src/App/Components/General/LoadingBox/LoadingBox';
import { ETTTemplatePreview } from '@src/App/Components/Settings/EmployeeTalkTemplate';
import {
    ETTEmployeeTalkProvider,
    useEmployeeTalk,
} from '@src/App/Components/Settings/EmployeeTalkTemplate/TemplateComponents/ETTEmployeeTalkProvider';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

const iconMap: Record<string, React.ComponentType<{ sx?: { fontSize: number } }>> = {
    Psychology,
    CloudUpload,
    Person,
    Assessment,
    Visibility,
    Celebration,
};

/**
 * Represents the EmployeeTalkPage component.
 *
 * @returns {JSX.Element} The JSX element for the EmployeeTalkPage.
 */
export default function EmployeeTalkPage() {
    const { id } = useParams<{ id: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    if (!id) {
        return (
            <InnerLayout title={langStrings.employeeTalks}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" color="error">
                        {langStrings.noIdProvided}
                    </Typography>
                </Box>
            </InnerLayout>
        );
    }

    return (
        <ETTEmployeeTalkProvider employeeTalkId={Number(id)}>
            <EmployeeTalkPageUI theme={theme} isMobile={isMobile} isSmallMobile={isSmallMobile} />
        </ETTEmployeeTalkProvider>
    );
}

interface EmployeeTalkPageUIProps {
    theme: Theme;
    isMobile: boolean;
    isSmallMobile: boolean;
}

const EmployeeTalkPageUI: React.FC<EmployeeTalkPageUIProps> = ({ theme, isMobile, isSmallMobile }) => {
    const employeeTalkContext = useEmployeeTalk();
    const [chatDialogOpen, setChatDialogOpen] = React.useState(false);
    const {
        employeeTalk,
        template,
        isOnboarding,
        loading,
        error,
        currentStep,
        showAgent,
        progress,
        canProceedToNextStep,
        availableSteps,
        handleNext,
        handleBack,
        handleStepClick,
        getEmployeeName,
        formatDate,
        // secureState,
        approveEmployeeTalk,
        unapproveEmployeeTalk,
    } = employeeTalkContext;

    const currentEmployee = useSelector((state: AppState) => state.employee);
    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    if (loading) return <LoadingBox />;

    if (error || !employeeTalk) {
        return (
            <InnerLayout title={langStrings.employeeTalks}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" color="error">
                        {error || langStrings.errorLoadingEmployeeTalk}
                    </Typography>
                </Box>
            </InnerLayout>
        );
    }

    if (!template) {
        return (
            <InnerLayout title={langStrings.employeeTalks}>
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" color="error">
                        Template-Daten nicht verfügbar
                    </Typography>
                </Box>
            </InnerLayout>
        );
    }

    const getStatusChipColor = (status2: EEmployeeTalkStatus) => {
        switch (status2) {
            case EEmployeeTalkStatus.PLANNED:
                return 'primary';
            case EEmployeeTalkStatus.COMPLETED:
                return 'success';
            case EEmployeeTalkStatus.CANCELLED:
                return 'error';
            case EEmployeeTalkStatus.POSTPONED:
                return 'warning';
            default:
                return 'default';
        }
    };

    // NAVIGATION DEBUG: These handlers include debug logging to track navigation issues
    // CONTEXT: Navigation was completely broken due to WebSocket subscription loss
    // SOLUTION: Added logging to identify when navigation functions aren't working
    const handleNextClick = () => {
        console.log('🔼 Header Next Click:', { currentStep, canProceedToNextStep, availableSteps });
        if (!canProceedToNextStep) {
            console.log('❌ Cannot proceed to next step');
            return;
        }
        handleNext();
    };

    const handleBackClick = () => {
        console.log('🔽 Header Back Click:', { currentStep });
        handleBack();
    };

    const handleStepClickInternal = (stepIndex: number) => {
        console.log('👆 Stepper Click:', { stepIndex, currentStep, availableSteps });
        handleStepClick(stepIndex);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', px: 1 }}>
            {/* Futuristic Header */}
            <Paper
                elevation={0}
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    borderRadius: 3,
                    p: 3,
                    mb: 4,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 48,
                            height: 48,
                        }}
                    >
                        <Psychology />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {employeeTalk.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {template.description}
                        </Typography>
                    </Box>
                </Box>

                {/* Employee Talk Info */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                    <Chip
                        label={`Status: ${employeeTalk.status2}`}
                        color={getStatusChipColor(employeeTalk.status2)}
                        size="small"
                    />
                    <Chip
                        label={`Mitarbeiter: ${getEmployeeName(employeeTalk.employeeId)}`}
                        variant="outlined"
                        size="small"
                    />
                    <Chip
                        label={`Manager: ${getEmployeeName(employeeTalk.meetingPartnerId)}`}
                        variant="outlined"
                        size="small"
                    />
                    <Chip label={`Datum: ${formatDate(employeeTalk.scheduledDate)}`} variant="outlined" size="small" />

                    {/* Approval Status */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                        {/* Employee Approval - MUST BE FIRST */}
                        <Chip
                            icon={employeeTalk.employeeConfirmed ? <CheckCircle /> : <HourglassEmpty />}
                            label="Employee"
                            color={employeeTalk.employeeConfirmed ? 'success' : 'default'}
                            size="small"
                            deleteIcon={
                                currentEmployee.id === employeeTalk.employeeId && !employeeTalk.employeeConfirmed ? (
                                    <Check />
                                ) : undefined
                            }
                            onDelete={
                                currentEmployee.id === employeeTalk.employeeId && !employeeTalk.employeeConfirmed
                                    ? async () => {
                                          try {
                                              await approveEmployeeTalk('employee');
                                          } catch (error) {
                                              setErrorMessage(
                                                  error instanceof Error ? error.message : 'Fehler beim Bestätigen',
                                              );
                                          }
                                      }
                                    : undefined
                            }
                            onClick={
                                currentEmployee.id === employeeTalk.employeeId &&
                                employeeTalk.employeeConfirmed &&
                                !(employeeTalk.employeeConfirmed && employeeTalk.partnerConfirmed)
                                    ? async () => {
                                          try {
                                              await unapproveEmployeeTalk('employee');
                                          } catch (error) {
                                              setErrorMessage(
                                                  error instanceof Error ? error.message : 'Fehler beim Zurücknehmen',
                                              );
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                cursor:
                                    currentEmployee.id === employeeTalk.employeeId &&
                                    !(employeeTalk.employeeConfirmed && employeeTalk.partnerConfirmed)
                                        ? 'pointer'
                                        : 'default',
                            }}
                        />

                        {/* Manager Approval - ONLY AFTER EMPLOYEE */}
                        <Chip
                            icon={employeeTalk.partnerConfirmed ? <CheckCircle /> : <HourglassEmpty />}
                            label="Manager"
                            color={employeeTalk.partnerConfirmed ? 'success' : 'default'}
                            size="small"
                            deleteIcon={
                                currentEmployee.id === employeeTalk.meetingPartnerId &&
                                !employeeTalk.partnerConfirmed ? (
                                    <Check />
                                ) : undefined
                            }
                            onDelete={
                                currentEmployee.id === employeeTalk.meetingPartnerId && !employeeTalk.partnerConfirmed
                                    ? async () => {
                                          try {
                                              await approveEmployeeTalk('manager');
                                          } catch (error) {
                                              setErrorMessage(
                                                  error instanceof Error ? error.message : 'Fehler beim Bestätigen',
                                              );
                                          }
                                      }
                                    : undefined
                            }
                            onClick={
                                currentEmployee.id === employeeTalk.meetingPartnerId &&
                                employeeTalk.partnerConfirmed &&
                                !(employeeTalk.employeeConfirmed && employeeTalk.partnerConfirmed)
                                    ? async () => {
                                          try {
                                              await unapproveEmployeeTalk('manager');
                                          } catch (error) {
                                              setErrorMessage(
                                                  error instanceof Error ? error.message : 'Fehler beim Zurücknehmen',
                                              );
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                cursor:
                                    currentEmployee.id === employeeTalk.meetingPartnerId &&
                                    !(employeeTalk.employeeConfirmed && employeeTalk.partnerConfirmed)
                                        ? 'pointer'
                                        : 'default',
                            }}
                        />
                    </Box>
                </Box>

                {/* Progress Bar with Navigation */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Fortschritt
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            },
                        }}
                    />
                </Box>

                {/* Navigation Stepper */}
                <MobileStepper
                    variant="text"
                    steps={template.pages.length}
                    position="static"
                    activeStep={currentStep}
                    sx={{
                        backgroundColor: 'transparent',
                        p: 0,
                    }}
                    style={{ border: 'none' }}
                    nextButton={
                        <Button
                            size="small"
                            onClick={handleNextClick}
                            disabled={currentStep === template.pages.length - 1 || !canProceedToNextStep}
                            endIcon={<NavigateNext />}
                            sx={{
                                textTransform: 'none',
                                opacity: canProceedToNextStep ? 1 : 0.5,
                            }}
                            title={canProceedToNextStep ? '' : 'Bitte laden Sie zuerst Ihren Lebenslauf hoch'}
                        >
                            Weiter
                        </Button>
                    }
                    backButton={
                        <Button
                            size="small"
                            onClick={handleBackClick}
                            disabled={currentStep === 0}
                            startIcon={<NavigateBefore />}
                            sx={{ textTransform: 'none' }}
                        >
                            Zurück
                        </Button>
                    }
                />
            </Paper>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 3,
                    minHeight: '60vh',
                }}
            >
                {/* Desktop Stepper */}
                {!isMobile && (
                    <Paper
                        elevation={0}
                        sx={{
                            width: 280,
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: alpha(theme.palette.background.paper, 0.7),
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Stepper activeStep={currentStep} orientation="vertical">
                            {template.pages.map((page, index: number) => (
                                <Step key={page.id}>
                                    <StepLabel
                                        icon={
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor:
                                                        index <= currentStep
                                                            ? theme.palette.primary.main
                                                            : 'transparent',
                                                    border: `2px solid ${index <= currentStep ? theme.palette.primary.main : theme.palette.divider}`,
                                                    color:
                                                        index <= currentStep ? 'white' : theme.palette.text.secondary,
                                                    cursor: availableSteps.includes(index) ? 'pointer' : 'default',
                                                }}
                                                onClick={() => handleStepClickInternal(index)}
                                            >
                                                {index < currentStep ? (
                                                    <CheckCircle sx={{ fontSize: 18 }} />
                                                ) : (
                                                    (() => {
                                                        const IconComponent = page.icon ? iconMap[page.icon] : null;
                                                        return IconComponent ? (
                                                            <IconComponent sx={{ fontSize: 18 }} />
                                                        ) : (
                                                            index + 1
                                                        );
                                                    })()
                                                )}
                                            </Avatar>
                                        }
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: index === currentStep ? 600 : 400,
                                                color:
                                                    index === currentStep
                                                        ? theme.palette.primary.main
                                                        : index < currentStep
                                                          ? theme.palette.text.primary
                                                          : theme.palette.text.secondary,
                                                cursor: availableSteps.includes(index) ? 'pointer' : 'default',
                                            }}
                                            onClick={() => handleStepClickInternal(index)}
                                        >
                                            {page.title}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>
                )}

                {/* Main Content */}
                <Box sx={{ flex: 1 }}>
                    {/* Agent Section - Outside Paper */}
                    {showAgent && template?.pages[currentStep]?.aiAssistant?.enabled && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                                backdropFilter: 'blur(10px)',
                                overflow: 'visible',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            <OnboardingAgent
                                message={template.pages[currentStep].aiAssistant!.agentText}
                                step={currentStep}
                                onClose={() => console.log('Close agent - TODO: implement')}
                                stepName={template.pages[currentStep].title}
                                onOpenMainChat={() => setChatDialogOpen(true)}
                                employeeTalkId={employeeTalk.id}
                            />
                        </Paper>
                    )}

                    <Paper
                        elevation={0}
                        sx={{
                            p: isMobile ? 2 : 3,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            background: alpha(theme.palette.background.paper, 0.9),
                            backdropFilter: 'blur(10px)',
                            minHeight: '60vh',
                        }}
                    >
                        <ETTTemplatePreview
                            key={currentStep}
                            template={template}
                            initialStep={currentStep}
                            onStepChange={(step) => console.log('Step change:', step)}
                            isOnboarding={isOnboarding}
                            onCVUploadStateChange={(step, uploaded) => console.log('CV upload state:', step, uploaded)}
                            providerContext={employeeTalkContext}
                            employeeTalkId={employeeTalk.id}
                        />
                    </Paper>
                </Box>
            </Box>

            {/* Mobile Stepper - Fixed Bottom */}
            {isMobile && (
                <MobileStepper
                    variant={isSmallMobile ? 'dots' : 'progress'}
                    steps={template.pages.length}
                    position="bottom"
                    activeStep={currentStep}
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        zIndex: 999,
                        px: isSmallMobile ? 1 : 2,
                    }}
                    nextButton={
                        <IconButton
                            size={isSmallMobile ? 'small' : 'medium'}
                            onClick={handleNextClick}
                            disabled={currentStep === template.pages.length - 1 || !canProceedToNextStep}
                            sx={{
                                opacity: canProceedToNextStep ? 1 : 0.5,
                            }}
                            title={canProceedToNextStep ? '' : 'Bitte laden Sie zuerst Ihren Lebenslauf hoch'}
                        >
                            <NavigateNext />
                        </IconButton>
                    }
                    backButton={
                        <IconButton
                            size={isSmallMobile ? 'small' : 'medium'}
                            onClick={handleBackClick}
                            disabled={currentStep === 0}
                        >
                            <NavigateBefore />
                        </IconButton>
                    }
                />
            )}

            {/* Debug Panel - Always visible
      <EmployeeTalkDebugPanel
        employeeTalk={employeeTalk}
        providerState={secureState}
        providerContext={employeeTalkContext}
        isMobile={isMobile}
      />
      */}

            {/* Onboarding Chat FAB */}
            {isOnboarding && (
                <OnboardingChatButton
                    employeeTalkId={employeeTalk.id}
                    currentStep={currentStep}
                    stepName={template.pages[currentStep].title}
                    open={chatDialogOpen}
                    onOpenChange={setChatDialogOpen}
                />
            )}

            {/* Error Snackbar */}
            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={() => setErrorMessage('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setErrorMessage('')} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};
